/**
 * Target frame (top centre, Aion style): a gold/silver portrait ring with the level badge, the
 * name and rank, a thin HP bar with the percentage, the target's cast bar with its spell icon,
 * debuff and buff rows with real icons, and a target-of-target mini frame to the right.
 *
 * Per frame it only touches what changed (kit StatBar / AuraIcon, interned strings); the
 * portrait and badge are rebuilt only when the target changes.
 */
import type { ContentRegistry, DeepReadonly, DispelType, Unit, UnitId, World } from '@keystone/sim';
import { hudNow } from '../../game/prediction';
import { el, setClass, setStyle, setText } from '../dom';
import { shortNumber } from '../format';
import { StatBar } from '../kit';
import { FrameAuraRow } from './auraRows';
import { FrameCastBar } from './castBar';
import { portraitSvg } from './glyphs';
import { PCT, dispelTypesFor, hpPercent, nameColor, unitBadge } from './model';

/** Sum of absorb shields on a unit. */
function absorbOf(u: DeepReadonly<Unit>): number {
  let s = 0;
  for (const a of u.auras) s += a.absorbRemaining ?? 0;
  return s;
}

export class TargetFrame {
  readonly root: HTMLElement;
  private readonly portrait: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly name: HTMLElement;
  private readonly rank: HTMLElement;
  private readonly hpText: HTMLElement;
  private readonly pct: HTMLElement;
  private readonly hp: StatBar;
  private readonly cast: FrameCastBar;
  private readonly debuffs: FrameAuraRow;
  private readonly buffs: FrameAuraRow;
  private readonly tot: HTMLElement;
  private readonly totName: HTMLElement;
  private readonly totHp: StatBar;
  private shownId: UnitId | null = null;
  private totId: UnitId | null = null;
  private portraitKey = '';
  private levelKey = -1;
  private hpShown = NaN;
  private maxShown = NaN;
  private pctShown = -1;
  private alive = true;

  constructor(parent: HTMLElement, onSelect?: (id: UnitId) => void) {
    this.root = el('div', 'ft empty');
    const main = el('div', 'ft-main', this.root);
    this.portrait = el('div', 'ft-portrait', main);
    this.badge = el('div', 'ft-badge hk-num', this.portrait);
    const info = el('div', 'ft-info', main);
    const head = el('div', 'ft-head', info);
    this.name = el('div', 'ft-name', head);
    this.rank = el('div', 'ft-rank', head);
    const bar = el('div', 'ft-hp', info);
    this.hp = new StatBar(bar, { text: 'none', color: 'var(--hk-hp-grad)', extra: true, className: 'ft-hpbar' });
    const nums = el('div', 'ft-nums', info);
    this.hpText = el('div', 'ft-hptext hk-num', nums);
    this.pct = el('div', 'ft-pct hk-num', nums);
    this.cast = new FrameCastBar(info, { className: 'ft-cast' });
    const auras = el('div', 'ft-auras', this.root);
    this.debuffs = new FrameAuraRow(auras, { kind: 'debuff', max: 10, size: 'sm', className: 'ft-debuffs' });
    this.buffs = new FrameAuraRow(auras, { kind: 'buff', max: 12, size: 'xs', className: 'ft-buffs' });

    this.tot = el('div', 'ft-tot hk-hit empty', main);
    const totHead = el('div', 'ft-tot-head', this.tot);
    el('span', 'ft-tot-arrow', totHead, '▸');
    this.totName = el('span', 'ft-tot-name', totHead);
    this.totHp = new StatBar(this.tot, { text: 'none', height: 'sm', ghost: false, className: 'ft-tot-bar' });
    if (onSelect) {
      this.tot.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        if (this.totId) onSelect(this.totId);
      });
    }
    parent.appendChild(this.root);
  }

  /** Brief "target changed" flash (Web Animations, compositor-only). */
  private flash(): void {
    this.root.animate?.([{ filter: 'brightness(1.7)', transform: 'scale(1.03)' }, { filter: 'brightness(1)', transform: 'scale(1)' }], { duration: 170, easing: 'cubic-bezier(.2,.8,.2,1)' });
  }

  update(world: DeepReadonly<World>, content: ContentRegistry, targetId: UnitId | null, playerId: UnitId | null, realNow: number): void {
    const u = targetId ? world.units[targetId] : undefined;
    const changed = (u?.id ?? null) !== this.shownId;
    this.shownId = u?.id ?? null;
    setClass(this.root, 'empty', !u);
    if (!u) {
      this.totId = null;
      return;
    }
    if (changed) this.onNewUnit(u, world, content);
    const lvl = world.mplus?.level ?? 0;
    if (lvl !== this.levelKey) {
      this.levelKey = lvl;
      const b = unitBadge(u, world, content);
      setText(this.badge, b.level);
    }
    if (u.alive !== this.alive) {
      this.alive = u.alive;
      setClass(this.root, 'dead', !u.alive);
    }
    // HP: bar every frame (cheap, write-if-changed inside), text only when numbers change.
    const absorb = absorbOf(u);
    if (changed) this.hp.snap(u.hp, u.maxHp, realNow, absorb);
    else this.hp.update(u.hp, u.maxHp, realNow, absorb);
    if (u.hp !== this.hpShown || u.maxHp !== this.maxShown) {
      this.hpShown = u.hp;
      this.maxShown = u.maxHp;
      this.hpText.textContent = u.alive ? `${shortNumber(Math.max(0, Math.round(u.hp)))} / ${shortNumber(u.maxHp)}` : 'Dead';
    }
    const p = u.alive ? hpPercent(u.hp, u.maxHp) : 0;
    if (p !== this.pctShown) {
      this.pctShown = p;
      this.pct.textContent = PCT[p]!;
    }
    this.cast.update(u, world, content);
    const now = hudNow(world);
    const me = playerId ? world.units[playerId] : undefined;
    const dispel: ReadonlySet<DispelType> | null = u.faction === 'party' && me?.classId ? dispelTypesFor(content.class(me.classId)) : null;
    this.debuffs.update(world, content, u, playerId, now, dispel);
    this.buffs.update(world, content, u, playerId, now);
    this.updateTot(u, world, content, realNow);
  }

  private onNewUnit(u: DeepReadonly<Unit>, world: DeepReadonly<World>, content: ContentRegistry): void {
    this.flash();
    const b = unitBadge(u, world, content);
    this.levelKey = world.mplus?.level ?? 0;
    setText(this.badge, b.level);
    setText(this.name, u.name);
    setStyle(this.name, 'color', nameColor(u, content));
    setText(this.rank, b.rank);
    for (const t of ['boss', 'elite', 'normal', 'friendly'] as const) setClass(this.root, t, b.tone === t);
    setClass(this.root, 'enemy', u.faction === 'enemy');
    this.hp.setColor(u.faction === 'enemy' ? 'var(--hk-hp-grad)' : 'var(--hk-hp-friendly-grad)');
    const key = `${u.classId ?? ''}|${u.tier ?? ''}`;
    if (key !== this.portraitKey) {
      this.portraitKey = key;
      this.portrait.querySelector(':scope > svg')?.remove(); // +autoattack: only the portrait glyph, not the attack badge's icon
      this.portrait.insertBefore(portraitSvg(u.classId, u.tier), this.badge);
    }
    this.alive = u.alive;
    setClass(this.root, 'dead', !u.alive);
    this.hpShown = this.maxShown = NaN;
    this.pctShown = -1;
  }

  private updateTot(u: DeepReadonly<Unit>, world: DeepReadonly<World>, content: ContentRegistry, realNow: number): void {
    // Enemies: their threat victim (else target). Friends: their selected target.
    const id = (u.faction === 'enemy' ? (u.threat?.victimId ?? u.targetId) : u.targetId) ?? null;
    const t = id && id !== u.id ? world.units[id] : undefined;
    const tid = t?.alive ? t.id : null;
    if (tid !== this.totId) {
      this.totId = tid;
      setClass(this.tot, 'empty', !t || !tid);
      if (t && tid) {
        setText(this.totName, t.name);
        setStyle(this.totName, 'color', nameColor(t, content));
        this.totHp.setColor(t.faction === 'enemy' ? 'var(--hk-hp-grad)' : 'var(--hk-hp-friendly-grad)');
        this.totHp.snap(t.hp, t.maxHp, realNow);
      }
    }
    if (t && tid) this.totHp.update(t.hp, t.maxHp, realNow);
  }
}
